import { throwNotInstance, throwNull, throwUndefined } from '@esm-test/guards';
import { TestParserOptions } from '../../options/testParserOptions.js';
import { TestCollection } from '../../parser/models/testCollection.js';
import { Test } from '../../parser/models/test.js';
import { isClassDefinition } from '../../parser/utils/classUtils.js';
import { isModule } from '../../parser/utils/moduleUtils.js';

/**
 * Contextual information used during the post-processing phase of test extraction.
 */
export class PostProcessContext {
  /** The original module or object being parsed. */
  moduleToParse: any;
  /** The configuration options for the parser. */
  options: TestParserOptions;
  /** The collection of tests generated during pre-processing. */
  preTests: TestCollection;
  /** The final collection of processed tests being built. */
  postTests: TestCollection;

  /**
   * Initializes a new PostProcessContext.
   * @param moduleToParse - The source module or object.
   * @param options - Parser options.
   * @param preTests - Tests from pre-processing.
   * @param postTests - Target collection for final tests.
   */
  constructor(moduleToParse: any, options: TestParserOptions, preTests: TestCollection, postTests: TestCollection) {
    throwUndefined("moduleToParse", moduleToParse);
    throwNull("moduleToParse", moduleToParse);

    if (!isModule(moduleToParse) && !isClassDefinition(moduleToParse)) {
      throwNotInstance("moduleToParse", moduleToParse, Object);
    }

    throwNotInstance("options", options, TestParserOptions);
    throwNotInstance("preTests", preTests, TestCollection);
    throwNotInstance("postTests", postTests, TestCollection);

    this.moduleToParse = moduleToParse;
    this.options = options;
    this.preTests = preTests;
    this.postTests = postTests;
  }

  /**
   * Finds the next test in the pre-processed collection that matches any of the specified type bits.
   * 
   * @param startIndex - The index to start searching from.
   * @param type - The bitmask flags to check for.
   * @returns The matching test or null if not found.
   */
  nextHasEither(startIndex: number, ...type: number[]): Test | null {
    const props = this.preTests;
    for (let index = startIndex; index < props.length; index++) {
      const prop = props[index];
      if (prop.type.hasEither(...type)) {
        return prop;
      }
    }
    return null;
  }

}
